import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';

import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BlockAction, ViewPart, ViewState } from '@core/models';
import { SubSink } from '@core/utils/subsink';
import { Block } from '@core/models/block/block.model';
import { BlockActionType } from '@core/enums';
import { ButtonHandlerService } from '@core/services/button-handler.service';
import { MixedObject } from '@core/interfaces/mixed-object';
import { CurrencyPreferenceService } from '@shared/services/currency/currency-preference.service';
import { BACK_TO_LIST_KEEP_ON_RELOAD_STORAGE_KEY } from '@modules/main/helpers/back-to-list-navigation.helper';
import { SessionStorageService } from '@shared/services/storage';

// ** The dropdown styles animations */
export const dropDownAnimations = trigger('dropDownAnimations', [
  transition(':enter', [
    style({opacity: 0}),
    animate('100ms', style({opacity: 1})),
  ]),
  transition(':leave', [
    animate('100ms', style({opacity: 0}))
  ])
]);

/**
 * Filter open/select currency actions
 */
const filterOpen = (action: BlockAction) => action.actionType === BlockActionType.OPEN;
const filterSelectCurrency = (action: BlockAction) => action.actionType === BlockActionType.REDIRECT;

@Component({
    selector: 'currency-switcher',
    template: `
    <div #currencyDropdown class="currency-dropdown" *ngIf="clientHasMultipleCurrencies">
      <div app-block
           *ngIf="dropDownToggleCurrencies"
           [class]="dropDownToggleCurrencies.uId"
           [item]="dropDownToggleCurrencies"
           [appClickableBlock]="dropDownToggleCurrencies">
      </div>
      <div #dropDownList
           app-block
           class="drop-down"
           *ngIf="_isOpen"
           [@dropDownAnimations]
           [class]="dropDownDownCurrencies.uId"
           [class.drop-down--up]="openUpwards"
           [item]="dropDownDownCurrencies">
      </div>
    </div>
  `,
    styleUrls: ['./currency-switcher.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: [dropDownAnimations],
    providers: [ButtonHandlerService],
    standalone: false
})
export class CurrencySwitcherComponent implements OnInit, OnDestroy {
  @Input() specialBlock: Block;
  _isOpen = false;
  openUpwards = false;
  dropDownToggleCurrencies: Block | undefined;
  dropDownDownCurrencies: Block | undefined;
  buttonClick$: Observable<BlockAction> = this.buttonService.click$;
  @ViewChild('currencyDropdown', {read: ElementRef}) currencyDropdown: ElementRef;
  @ViewChild('dropDownList', {read: ElementRef}) dropDownList: ElementRef;
  private subSink = new SubSink();

  constructor(private buttonService: ButtonHandlerService,
              private currencyPreferenceService: CurrencyPreferenceService,
              private storageService: SessionStorageService) {
  }

  get clientHasMultipleCurrencies(): boolean {
    return (this.specialBlock?.settings?.availableCurrencies ?? []).length > 1;
  }

  // clicked outside the box
  @HostListener('document:click', ['$event'])
  documentClick(event) {
    if (this.currencyDropdown && !this.currencyDropdown.nativeElement.contains(event.target) && this._isOpen) {
      this._isOpen = false;
    }
  }

  ngOnInit() {
    this.initialize();

    this.subSink.sink =
      this.buttonClick$
        .pipe(
          filter(filterSelectCurrency),
          map(({actionData}) => actionData),
        ).subscribe((actionData: MixedObject) => {
          if (actionData?.currency) {
            this.changeCurrency(actionData.currency);
          }
        });
  }

  initialize() {
    const currentViewState = this.specialBlock.children
      .find(b => b.settings.viewState === ViewState.MAIN);

    this.dropDownToggleCurrencies = currentViewState.children
      .find(b => b.settings.viewPart === ViewPart.DROP_DOWN_TOGGLE_CURRENCIES);

    this._isOpen = false;

    this.dropDownDownCurrencies = currentViewState.children
      .find(b => b.settings.viewPart === ViewPart.DROP_DOWN_DOWN_CURRENCIES);

    this.subSink.sink =
      this.buttonClick$
        .pipe(
          filter(filterOpen))
        .subscribe(_ => this.toggleDropDown());
  }

  toggleDropDown() {
    this._isOpen = !this._isOpen;

    if (this._isOpen) {
      // Wysokość listy znamy dopiero po jej wyrenderowaniu, więc kierunek liczymy
      // w następnej klatce; lista wchodzi z animacji od przezroczystości, więc
      // ewentualny przeskok na górę nie jest widoczny.
      requestAnimationFrame(() => this.updateDropDownDirection());
    }
  }

  /**
   * Lista otwiera się pod przyciskiem. Gdy przełącznik stoi nisko (np. na dole
   * stopki) i lista nie mieści się do dolnej krawędzi okna, otwieramy ją do góry —
   * ale tylko wtedy, gdy nad przyciskiem jest więcej miejsca niż pod nim.
   */
  private updateDropDownDirection(): void {
    const switcher = this.currencyDropdown?.nativeElement as HTMLElement | undefined;
    const list = this.dropDownList?.nativeElement as HTMLElement | undefined;

    if (!this._isOpen || !switcher || !list) {
      return;
    }

    const {top, bottom} = switcher.getBoundingClientRect();
    const spaceBelow = window.innerHeight - bottom;
    const listHeight = list.getBoundingClientRect().height;

    this.openUpwards = listHeight > spaceBelow && top > spaceBelow;
  }

  private changeCurrency(currency: string): void {
    this.currencyPreferenceService.setManualCurrency(currency);
    // Przeliczenie waluty wymaga pobrania strony od nowa, ale dla odwiedzającego to
    // nadal ta sama strona. Znacznik mówi mechanizmowi "powrotu do listy", że to
    // przeładowanie techniczne, a nie nowe wejście, które kasuje zapamiętaną listę.
    this.storageService.setItem(BACK_TO_LIST_KEEP_ON_RELOAD_STORAGE_KEY, '1');
    window.location.assign(
      this.currencyPreferenceService.appendCurrencyToUrl(window.location.href),
    );
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }
}
